<section class="fr-page">
  <app-admin-page-header>
    <ng-container actions>
      @if (showCreate()) {
        <app-admin-button
          variant="primary"
          type="button"
          [disabled]="!canCreate()"
          (click)="openCreate()"
        >+ Request Feature</app-admin-button>
      }
    </ng-container>
  </app-admin-page-header>

  <div class="fr-page__filters">
    <label class="cg-admin-field fr-page__search">
      <span class="cg-admin-field__label">Search</span>
      <input class="cg-input" type="search" [(ngModel)]="searchInput" (ngModelChange)="onSearchChange()" name="search" placeholder="Request ID, title, description" />
    </label>
    <label class="cg-admin-field">
      <span class="cg-admin-field__label">Status</span>
      <select class="cg-select" [(ngModel)]="statusFilter" (ngModelChange)="onFilterChange()" name="status">
        <option value="">Open requests</option>
        @for (st of statuses; track st) {
          <option [value]="st">{{ statusLabels[st] }}</option>
        }
      </select>
    </label>
    <label class="cg-admin-field">
      <span class="cg-admin-field__label">Type</span>
      <select class="cg-select" [(ngModel)]="typeFilter" (ngModelChange)="onFilterChange()" name="type">
        <option value="">All</option>
        @for (t of types; track t) {
          <option [value]="t">{{ typeLabels[t] }}</option>
        }
      </select>
    </label>
    <label class="cg-admin-field">
      <span class="cg-admin-field__label">Priority</span>
      <select class="cg-select" [(ngModel)]="priorityFilter" (ngModelChange)="onFilterChange()" name="priority">
        <option value="">All</option>
        @for (p of priorities; track p) {
          <option [value]="p">{{ p }}</option>
        }
      </select>
    </label>
    <label class="cg-admin-field">
      <span class="cg-admin-field__label">Assigned To</span>
      <select class="cg-select" [(ngModel)]="assignedToFilter" (ngModelChange)="onFilterChange()" name="assignedTo">
        <option value="">All</option>
        @for (u of assignableUsers(); track u.userId) {
          <option [value]="u.userId">{{ u.name }}</option>
        }
      </select>
    </label>
    <app-admin-button class="fr-page__reset" variant="ghost" type="button" (click)="resetFilters()">Reset Filters</app-admin-button>
  </div>

  @if (error()) {
    <div class="cg-admin-alert cg-admin-alert--error">{{ error() }}</div>
  }

  <app-admin-table>
    <thead>
      <tr>
        <th><button type="button" class="fr-page__sort" (click)="toggleSort('requestNumber')">Request ID{{ sortIndicator('requestNumber') }}</button></th>
        <th><button type="button" class="fr-page__sort" (click)="toggleSort('title')">Title{{ sortIndicator('title') }}</button></th>
        <th><button type="button" class="fr-page__sort" (click)="toggleSort('type')">Type{{ sortIndicator('type') }}</button></th>
        <th><button type="button" class="fr-page__sort" (click)="toggleSort('priority')">Priority{{ sortIndicator('priority') }}</button></th>
        <th><button type="button" class="fr-page__sort" (click)="toggleSort('status')">Status{{ sortIndicator('status') }}</button></th>
        <th>Assigned To</th>
        <th>Created By</th>
        <th><button type="button" class="fr-page__sort" (click)="toggleSort('createdAt')">Created{{ sortIndicator('createdAt') }}</button></th>
        <th><button type="button" class="fr-page__sort" (click)="toggleSort('updatedAt')">Last Updated{{ sortIndicator('updatedAt') }}</button></th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      @if (loading()) {
        <tr><td colspan="10"><p class="cg-form-help">Loading…</p></td></tr>
      } @else if (items().length === 0) {
        <tr>
          <td colspan="10">
            <app-admin-empty-state icon="inbox" title="No feature requests" description="Submit a request to get started." />
          </td>
        </tr>
      } @else {
        @for (item of items(); track item._id) {
          <tr class="fr-page__row" (click)="goToDetail(item, $event)">
            <td><code>{{ item.requestNumber }}</code></td>
            <td><strong>{{ item.title }}</strong></td>
            <td>{{ typeLabels[item.type] }}</td>
            <td><app-feature-request-priority-badge [priority]="item.priority" size="sm" /></td>
            <td><app-feature-request-status-badge [status]="item.status" size="sm" /></td>
            <td>{{ item.assignedToUser?.displayName ?? '—' }}</td>
            <td>{{ item.createdByUser?.displayName ?? '—' }}</td>
            <td>{{ item.createdAt | date: 'd MMM' }}</td>
            <td>{{ item.updatedAt | date: 'd MMM' }}</td>
            <td class="fr-page__actions">
              <a class="cg-admin-link" [routerLink]="['/platform/feature-requests', item._id]" (click)="$event.stopPropagation()">View</a>
              @if (showStatusChange()) {
                <select
                  class="cg-select fr-page__status-select"
                  [ngModel]="item.status"
                  (ngModelChange)="onInlineStatusChange(item, $event)"
                  [name]="'fr-status-' + item._id"
                  [disabled]="!canStatusChange()"
                  (click)="$event.stopPropagation()"
                >
                  @for (st of statuses; track st) {
                    <option [value]="st">{{ statusLabels[st] }}</option>
                  }
                </select>
              }
            </td>
          </tr>
        }
      }
    </tbody>
  </app-admin-table>

  <div class="fr-page__pagination">
    <span>{{ pageSummary() }}</span>
    <label>
      Rows
      <select class="cg-select" [(ngModel)]="pageSize" (ngModelChange)="onPageSizeChange()" name="pageSize">
        @for (size of pageSizeOptions; track size) {
          <option [value]="size">{{ size }}</option>
        }
      </select>
    </label>
    <app-admin-button variant="ghost" type="button" [disabled]="page() <= 1" (click)="prevPage()">Previous</app-admin-button>
    <app-admin-button variant="ghost" type="button" [disabled]="!hasNextPage()" (click)="nextPage()">Next</app-admin-button>
  </div>

  <app-feature-request-create-modal
    [open]="createOpen()"
    [busy]="createSaving()"
    [error]="createError()"
    (closed)="closeCreate()"
    (submitted)="onCreateSubmitted($event)"
  />
</section>
